import { SecureRoute } from '@okta/okta-react';
import React, { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { ObjectType } from 'api/pushes';
import { defaultFilter } from 'components/molecules/filters/utils';
import { Page } from 'components/molecules/page';
import { updateDynamicTemplateConditionsAction } from 'data/templates/projects/actions';
import {
  selectParentName,
  selectPhaseName,
} from 'data/templates/projects/selectors';
import { updateProjectTemplateProjectPhase } from 'data/templates/projects/slices';
import { TemplateDetailHeader } from '../../commons';
import { DynamicConditionsDrawer } from './dynamic-conditions-drawer';
import { ProjectDynamicTemplatePreview } from './preview';
import { ProjectTemplatePlanList } from './table';
import { useDynamicConditionsColumn } from './use-dynamic-conditions-column';

const StyledBreadCrumbSubTitle = styled.p`
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-01);
`;

const StyledPhaseName = styled.span`
  margin-right: var(--spacing-03);
`;

const StyledParentName = styled.span`
  margin-left: var(--spacing-03);
`;

export const ProjectDynamicTemplate: FC = () => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const [showConditionsDrawer, setShowConditionsDrawer] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<null | { id: number; title; string; type: string }>(null);
  const [selectedRecordFilter, setSelectedRecordFilter] =
    useState(defaultFilter);

  const onAddConditionsClick = (record) => {
    setSelectedRecord(record);
    setSelectedRecordFilter({ ...defaultFilter });
    setShowConditionsDrawer(true);
  };

  const onEditConditionsClick = (record, filter) => {
    setSelectedRecord(record);
    setSelectedRecordFilter({ ...filter });
    setShowConditionsDrawer(true);
  };
  const onDeleteConditionsConfirm = (record) => {
    dispatch(
      updateDynamicTemplateConditionsAction({
        entityId: record?.id,
        conditions: defaultFilter,
      }),
    );
  };
  const onCloseDrawer = () => {
    setShowConditionsDrawer(false);
  };
  const dynamicColumn = useDynamicConditionsColumn(
    onAddConditionsClick,
    onEditConditionsClick,
    onDeleteConditionsConfirm,
  );

  const onDynamicConditionsSubmit = (filter, shiftDaysInput) => {
    onCloseDrawer();
    if (selectedRecord) {
      dispatch(
        updateDynamicTemplateConditionsAction({
          entityId: selectedRecord?.id,
          conditions: filter,
        }),
      );
      if (selectedRecord.type === ObjectType.ProjectPhase) {
        dispatch(
          updateProjectTemplateProjectPhase({
            projectPhase: {
              projectPhaseId: selectedRecord.id,
              shiftDays: shiftDaysInput,
            },
          }),
        );
      }
    }
  };

  const Title = ({ _selectedRecord }) => {
    const { id, title, level } = _selectedRecord;
    const parentName = useSelector(selectParentName(id));
    const phaseName = useSelector(selectPhaseName(id));

    switch (level) {
      case 1:
        return (
          <>
            <StyledBreadCrumbSubTitle>{parentName}</StyledBreadCrumbSubTitle>
            <h5>{title}</h5>
          </>
        );
      case 2:
      case 3:
        return (
          <>
            <StyledBreadCrumbSubTitle>
              <StyledPhaseName>{phaseName}</StyledPhaseName> /
              <StyledParentName>{parentName}</StyledParentName>
            </StyledBreadCrumbSubTitle>
            <h5>{title}</h5>
          </>
        );
      case 0:
      default:
        return <h5>{title}</h5>;
    }
  };

  return (
    <Page>
      <Switch>
        <SecureRoute path={`${path}/preview`}>
          <ProjectDynamicTemplatePreview />
        </SecureRoute>
        <SecureRoute exact path={path}>
          <TemplateDetailHeader />
          <Page.Content>
            <ProjectTemplatePlanList additionalColumns={[dynamicColumn]} />
          </Page.Content>
          <DynamicConditionsDrawer
            filter={selectedRecordFilter}
            onSubmit={onDynamicConditionsSubmit}
            title={<Title _selectedRecord={selectedRecord} />}
            type={selectedRecord?.type}
            visible={showConditionsDrawer}
            onClose={onCloseDrawer}
          />
        </SecureRoute>
      </Switch>
    </Page>
  );
};
